import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/core/auth/AuthContext';
import { stockMovementsService } from '../services/stockMovements.service';
import type { StockMovementFilters } from '../types/stockMovements.types';

export function useStockMovements() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';

  const [filters, setFilters] = useState<StockMovementFilters>({
    search: '',
    transaction_type: 'all',
    date_from: '',
    date_to: '',
    page: 1,
    pageSize: 15,
  });

  const queryKey = ['inventory', 'transactions', companyId, filters.transaction_type, filters.date_from, filters.date_to];

  const query = useQuery({
    queryKey,
    queryFn: () => stockMovementsService.fetchMovements(companyId, filters.transaction_type, filters.date_from, filters.date_to),
    enabled: !!companyId,
  });

  const items = useMemo(() => {
    let result = query.data || [];
    
    if (filters.search) {
      const lowerSearch = filters.search.toLowerCase();
      result = result.filter(item => 
        item.item?.name.toLowerCase().includes(lowerSearch) ||
        item.item?.code.toLowerCase().includes(lowerSearch) ||
        item.note?.toLowerCase().includes(lowerSearch)
      );
    }

    const start = (filters.page - 1) * filters.pageSize;
    const end = start + filters.pageSize;
    return {
      paginated: result.slice(start, end),
      total: result.length
    };
  }, [query.data, filters.search, filters.page, filters.pageSize]);

  return {
    data: items.paginated,
    total: items.total,
    isLoading: query.isLoading,
    filters,
    setFilters,
    updateSearch: (search: string) => setFilters((f) => ({ ...f, search, page: 1 })),
    updateType: (transaction_type: string) => setFilters((f) => ({ ...f, transaction_type, page: 1 })),
    updateDateRange: (date_from: string, date_to: string) => setFilters((f) => ({ ...f, date_from, date_to, page: 1 })),
    updatePage: (page: number) => setFilters((f) => ({ ...f, page })),
  };
}
